import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { signFamilyAgreements } from 'api/account/signFamilyAgreements';
import { CommonOnboardingPage } from 'app-pages/pages/constants';
import { BackArrowButton } from 'components/_global/back-button/BackArrowButton';
import { Checkbox } from 'components/core/checkbox/Checkbox';
import { ExternalLink } from 'components/core/link/Link';
import { Text } from 'components/core/text';
import { mediaMobile } from 'components/core/theme/breakpoints';
import { FooterButton } from 'components/structure/footer/FooterButton';
import { FooterPortal } from 'components/structure/footer/FooterPortal';
import { useConsult } from 'contexts/ConsultContext';
import { useStripe } from 'hooks/useStripe';
import { stripeRedirect } from 'hooks/useStripeRedirect';
import { useNavigate } from 'react-router-dom';

import { NextStepActions, useNextStep } from '../../../../api/account/useNextStep';
import { Content } from '../Styles';
import { ContentWrapper } from './styles';

const AgreementContainer = styled.div`
  display: flex;

  & label {
    margin-top: 4px;
  }
`;

const Agreement = styled.div`
  padding-left: 20px;
`;

const HeadlineText = styled.div`
  padding-top: 80px;

  ${mediaMobile} {
    padding-top: 0px;
  }
`;

const NoPaddingWrapper = styled(ContentWrapper)`
  ${mediaMobile} {
    padding: 0;
  }
`;
interface NoShowCancellationPolicyProps {
  flow?: string;
}

const NoShowCancellationPolicy = ({ flow }: NoShowCancellationPolicyProps) => {
  const navigate = useNavigate();
  const { startStripe } = useStripe();
  const nextStep = useNextStep();
  const patientConsult = useConsult().consult;
  const [nextButtonText, setNextButtonText] = useState('Loading');
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [_error, setError] = useState<unknown>();

  const popNavigation = () => {
    navigate(-1);
  };

  useEffect(() => {
    if (
      patientConsult?.patient.creditCardInfoProvided ||
      patientConsult?.patient.isC3hs ||
      patientConsult?.skipCreditCard
    ) {
      setNextButtonText(ContinueButtonText);
    } else {
      setNextButtonText(AddCreditCardButtonText);
    }
  }, [patientConsult?.patient.creditCardInfoProvided, patientConsult?.patient.isC3hs, patientConsult?.skipCreditCard]);

  const redirectToStripe = async (consultId: string, patientId: string) => {
    try {
      await stripeRedirect(
        {
          id: consultId,
          patientId: patientId,
          priceId: '',
          flow: flow,
        },
        startStripe,
      );
    } catch (error) {
      setShowError(true);
      setError(error);
    }
  };

  const handleNext = async () => {
    if (!patientConsult) return;
    if (agreed) {
      setLoading(true);

      try {
        await signFamilyAgreements('NO_SHOW_AND_CANCELLATION_POLICY');
      } catch (error) {
        setShowError(true);
        setError(error);
        return;
      }
    }

    await nextStep
      .mutateAsync({
        page: CommonOnboardingPage.NoShowCancellationPolicy,
        patientId: patientConsult.patient.id,
        patientConsultId: patientConsult.id,
      })
      .then((r) => {
        if (r.url === NextStepActions.RedirectToStripe) {
          redirectToStripe(patientConsult.id, patientConsult?.patient.id);
        } else {
          navigate(`/consult/${patientConsult.id}/schedule/first-appointment`);
        }
      });
  };

  if (!patientConsult) return null;

  return (
    <Content>
      <NoPaddingWrapper style={{ gap: '24px' }}>
        <HeadlineText>
          <BackArrowButton onClick={popNavigation} />
          <Text variant="heading3">Please review and accept the following:</Text>
        </HeadlineText>
        <Text variant="text2" bold data-testid="no-show-cancellation-policy-title">
          No-Show and Late Cancellation Policy
        </Text>
        <AgreementContainer>
          <Checkbox
            labelStyle={{ alignItems: 'flex-start', marginTop: '6px' }}
            label=""
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            testId="no-show-and-cancellation-policy-checkbox"
          />
          <Agreement>
            <Text variant="text6">
              By checking the box, I acknowledge that I have read, understood, and agree to the terms of the{' '}
              <ExternalLink variant="text6" bold href="https://www.bendhealth.com/no-show">
                Bend Health No-Show and Late Cancellation Policy
              </ExternalLink>{' '}
              as outlined. I understand that failure to provide notice within 48 hours, as specified in the policy, may
              result in applicable fees or charges.
            </Text>
            {showError ? (
              <div>
                <p style={{ color: 'red' }}>There was an error, please try again</p>
              </div>
            ) : (
              <></>
            )}
          </Agreement>
        </AgreementContainer>
      </NoPaddingWrapper>
      <FooterPortal>
        <FooterButton disabled={!agreed} isLoading={loading} onClick={handleNext}>
          {nextButtonText}
        </FooterButton>
      </FooterPortal>
    </Content>
  );
};

const ContinueButtonText = 'Continue';
const AddCreditCardButtonText = 'Add credit card';

export default NoShowCancellationPolicy;
